import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  useUserDetail,
  useUserAnalytics,
  useUserProjects,
  useUserTasks,
  useUserTimeEntries,
  useTimeEntryStatistics,
  useWeeklyCompliance,
  useSystemSettings,
} from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';
import {
  ArrowLeft,
  User,
  Calendar,
  Clock,
  FolderOpen,
  CheckSquare,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Target,
  Activity,
  MapPin,
  Github,
  Mail,
  Shield,
  Users,
  BarChart3,
  PieChart,
  Clock as ClockIcon,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  Crown,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  ExternalLink,
  Zap,
  Timer,
  UserCheck,
  UserX,
  Briefcase,
  KeyRound,
  Copy,
  Check,
  CalendarDays,
  Bell,
} from 'lucide-react';
import SendNotificationModal from './SendNotificationModal';
import { format, formatDistanceToNow } from 'date-fns';
import { toUTCPlus5, toUTCPlus5Time } from '../utils/time';
import FormDrawer from './FormDrawer';
import ConfirmModal from './ConfirmModal';
import Avatar from './Avatar';
import Timesheet from './Timesheet';
import {
  Button,
  Panel,
  EmptyState,
  ViewTabs,
  StatCard,
  StatusPill,
} from './ui';

const USER_TABS = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'timesheet', label: 'Timesheet', icon: CalendarDays },
  { id: 'projects', label: 'Projects', icon: FolderOpen },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'time-entries', label: 'Time Entries', icon: Clock },
];

const ROLE_PILL_CLASS = {
  admin: 'bg-amber-50 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  project_lead: 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
  member: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-200',
};

const roleLabel = (role) => {
  if (role === 'project_lead') return 'Project Lead';
  if (role === 'admin') return 'Admin';
  if (role === 'member') return 'Member';
  return role || 'Unknown';
};

const taskStatusPill = (status) => {
  const s = (status || '').toLowerCase().replace(/\s+/g, '_');
  if (s === 'done' || s === 'completed') return 'Done';
  if (s === 'in_progress') return 'In Progress Soft';
  if (s === 'in_qa') return 'On Hold';
  if (s === 'blocked') return 'At Risk';
  if (s === 'to_do' || s === 'todo') return 'To Do';
  return 'Unknown';
};

const projectStatusPill = (status) => {
  const s = (status || 'active').toLowerCase();
  if (s === 'active' || s === 'in_progress' || s === 'in progress') return 'In Progress';
  if (s === 'completed' || s === 'done') return 'Completed';
  if (s === 'on_hold' || s === 'on hold' || s === 'paused') return 'On Hold';
  return 'In Progress';
};

const compliancePill = (status) => {
  if (status === 'on_track') return { status: 'Completed', label: 'On Track' };
  if (status === 'under_target') return { status: 'Delayed', label: 'Under Target' };
  return { status: 'At Risk', label: 'Critical' };
};

const complianceBarClass = (status) => {
  if (status === 'on_track') return 'bg-emerald-500';
  if (status === 'under_target') return 'bg-amber-500';
  return 'bg-red-500';
};

const formatWeekRange = (weekStart, weekEnd) => {
  try {
    const start = new Date(`${weekStart}T00:00:00`);
    const end = new Date(`${weekEnd}T00:00:00`);
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
  } catch {
    return `${weekStart} – ${weekEnd}`;
  }
};

const toYmd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { data: systemSettings } = useSystemSettings();
  const weeklyTarget = systemSettings?.weekly_target_hours ?? SYSTEM_SETTINGS_DEFAULTS.weekly_target_hours;

  // UI State (filters needed by data hooks)
  const [activeTab, setActiveTab] = useState('overview');
  const [analyticsTimePeriod, setAnalyticsTimePeriod] = useState('30_days');
  
  // Pagination and filtering state
  const [projectsPage, setProjectsPage] = useState(1);
  const [tasksPage, setTasksPage] = useState(1);
  const [timeEntriesPage, setTimeEntriesPage] = useState(1);
  const [pageSize] = useState(10);
  
  // Filter state
  const [projectsSearch, setProjectsSearch] = useState('');
  const [tasksSearch, setTasksSearch] = useState('');
  const [timeEntriesSearch, setTimeEntriesSearch] = useState('');
  const [tasksStatusFilter, setTasksStatusFilter] = useState('all');
  const [timeEntriesDateFilter, setTimeEntriesDateFilter] = useState('all');

  const timeEntriesQueryFilters = useMemo(() => {
    const filters = {
      page: timeEntriesPage,
      page_size: pageSize,
    };
    const q = timeEntriesSearch.trim();
    if (q) filters.search = q;

    if (timeEntriesDateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const weekStart = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      if (timeEntriesDateFilter === 'today') {
        filters.start_date = toYmd(today);
        filters.end_date = toYmd(today);
      } else if (timeEntriesDateFilter === 'yesterday') {
        filters.start_date = toYmd(yesterday);
        filters.end_date = toYmd(yesterday);
      } else if (timeEntriesDateFilter === 'this_week') {
        filters.start_date = toYmd(weekStart);
        filters.end_date = toYmd(today);
      }
    }

    return filters;
  }, [timeEntriesPage, pageSize, timeEntriesSearch, timeEntriesDateFilter]);

  const analyticsPeriodMap = {
    '7_days': 'last_30_days',
    '30_days': 'last_30_days',
    '90_days': 'last_90_days',
    '1_year': 'quarter',
  };
  const backendAnalyticsPeriod = analyticsPeriodMap[analyticsTimePeriod] || 'last_30_days';

  const { 
    data: userData, 
    isLoading: userLoading,
    isError: userError,
    refetch: fetchUser
  } = useUserDetail(userId);
  
  const { 
    data: userStatsData, 
    isLoading: statsLoading,
  } = useUserAnalytics(userId, {
    enabled: activeTab === 'analytics',
    period: backendAnalyticsPeriod,
  });
  
  const { 
    data: projectsData, 
    isLoading: projectsLoading,
  } = useUserProjects(userId, {
    enabled: activeTab === 'overview' || activeTab === 'projects',
  });
  
  const { 
    data: tasksData, 
    isLoading: tasksLoading,
  } = useUserTasks(userId, {
    enabled: activeTab === 'overview' || activeTab === 'tasks' || activeTab === 'analytics',
  });
  
  const { 
    data: timeEntriesData, 
    isLoading: timeEntriesLoading,
  } = useUserTimeEntries(
    userId,
    activeTab === 'analytics'
      ? { page_size: 200 }
      : timeEntriesQueryFilters,
    {
      enabled: activeTab === 'time-entries' || activeTab === 'analytics',
    }
  );

  const { data: timeEntryStats } = useTimeEntryStatistics(
    userId ? { user_id: userId } : {},
    { enabled: !!userId && (activeTab === 'overview' || activeTab === 'time-entries') }
  );

  const {
    data: weeklyCompliance,
    isLoading: weeklyComplianceLoading,
  } = useWeeklyCompliance(userId, 4, {
    enabled: activeTab === 'analytics',
  });
  
  // Extract data from React Query responses - memoized to prevent unnecessary re-renders
  const user = useMemo(() => userData || null, [userData]);
  const userStats = useMemo(() => userStatsData || null, [userStatsData]);
  const projects = useMemo(() => projectsData?.results || [], [projectsData]);
  const tasks = useMemo(() => tasksData?.results || [], [tasksData]);
  const timeEntries = useMemo(() => timeEntriesData?.results || [], [timeEntriesData]);
  const timeEntriesTotal =
    timeEntryStats?.total_entries ?? timeEntriesData?.total ?? timeEntries.length;
  const timeEntriesListTotal = timeEntriesData?.total ?? 0;
  const complianceWeeks = useMemo(
    () => weeklyCompliance?.weeks || [],
    [weeklyCompliance]
  );
  const complianceSummary = weeklyCompliance?.summary;
  
  // Edit user modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSendNotification, setShowSendNotification] = useState(false);
  const [editFormData, setEditFormData] = useState({
    department: '',
    team_lead_id: '',
    is_disabled: false
  });
  const [availableTeamLeads, setAvailableTeamLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [passwordCopied, setPasswordCopied] = useState(false);

  // Analytics derived from tab-gated React Query hooks (no duplicate manual fetch)
  const analyticsLoading = activeTab === 'analytics' && (statsLoading || timeEntriesLoading);
  const analyticsData = useMemo(() => {
    const analytics = userStatsData || {};
    const entries = timeEntriesData?.results || [];
    const taskList = analytics.tasks?.list || tasks;

    const taskStatusBreakdown = {
      to_do: taskList.filter((t) => t.status === 'To Do').length || 0,
      in_progress: taskList.filter((t) => t.status === 'In Progress').length || 0,
      in_qa: taskList.filter((t) => t.status === 'In QA').length || 0,
      done: taskList.filter((t) => t.status === 'Done').length || 0,
      blocked: taskList.filter((t) => t.status === 'Blocked').length || 0,
    };

    const completedTasks = analytics.tasks?.completed || 0;
    const totalHoursFromEntries = entries.reduce((sum, entry) => {
      return sum + ((entry.duration || 0) / 3600);
    }, 0);

    const efficiencyMetrics = {
      totalHours: totalHoursFromEntries,
      completedTasks,
      tasksPerHour: totalHoursFromEntries > 0 ? (completedTasks / totalHoursFromEntries).toFixed(2) : 0,
      averageTaskTime: completedTasks > 0 ? (totalHoursFromEntries / completedTasks).toFixed(2) : 0,
      productivityScore: Math.min(100, (completedTasks / Math.max(1, taskList.length || tasks.length || 1)) * 100).toFixed(1),
    };

    const leavePerformance = {
      sickLeaves: analytics.leave?.medical_consumed || 0,
      annualLeaves: analytics.leave?.annual_consumed || 0,
      utilization: analytics.leave?.utilization || 0,
    };

    const productivityTrends = entries
      .filter((entry) => entry.start_time && entry.duration)
      .map((entry) => ({
        date: new Date(entry.start_time).toISOString().split('T')[0],
        hours: entry.duration / 3600,
        description: entry.description || 'No description',
      }))
      .reduce((acc, entry) => {
        const existingEntry = acc.find((e) => e.date === entry.date);
        if (existingEntry) {
          existingEntry.hours += entry.hours;
        } else {
          acc.push(entry);
        }
        return acc;
      }, [])
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-7);

    return {
      taskStatusBreakdown,
      efficiencyMetrics,
      leavePerformance,
      timeEntries: entries,
      productivityTrends,
    };
  }, [userStatsData, timeEntriesData, tasks]);

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    const date = new Date(lastLogin);
    const pakistanTime = new Date(date.getTime() + (5 * 60 * 60 * 1000));
    return pakistanTime.toLocaleString('en-US', {
      timeZone: 'Asia/Karachi',
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Filtering functions
  const getFilteredProjects = () => {
    const q = projectsSearch.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((project) =>
      project.name?.toLowerCase().includes(q) ||
      project.description?.toLowerCase().includes(q)
    );
  };

  const getFilteredTasks = () => {
    const q = tasksSearch.trim().toLowerCase();
    return tasks.filter((task) => {
      const matchesSearch =
        !q ||
        task.name?.toLowerCase().includes(q) ||
        task.description?.toLowerCase().includes(q) ||
        task.project_name?.toLowerCase().includes(q);
      const status = task.status || '';
      const matchesStatus =
        tasksStatusFilter === 'all' ||
        status === tasksStatusFilter ||
        status.toLowerCase().replace(/\s+/g, '_') === tasksStatusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const projectsById = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      if (p?.id) map[p.id] = p;
    });
    return map;
  }, [projects]);

  const getTaskProjectName = (task) =>
    task.project_name ||
    projectsById[task.project_id]?.name ||
    '—';

  const safeLabel = (value, fallback = '—') => {
    if (value == null || value === '') return fallback;
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      return value.name || value.title || value.label || fallback;
    }
    return String(value);
  };

  const formatEntryDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '—';
    const total = Math.max(0, Math.floor(seconds));
    const hrs = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  const renderListPagination = ({ page, setPage, total, label }) => {
    const totalPages = Math.ceil(total / pageSize) || 1;
    if (totalPages <= 1) return null;
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50/50 dark:bg-secondary-800/50">
        <p className="text-xs text-secondary-500 dark:text-secondary-400">
          Showing {start}–{end} of {total} {label}
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
            className="p-1.5 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-white dark:hover:bg-secondary-700 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          {getPaginationPages(page, totalPages).map((p, index) =>
            p === '...' ? (
              <span key={`ellipsis-${index}`} className="px-2 text-xs text-secondary-400">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`min-w-[2rem] px-2 py-1 text-xs font-medium rounded-lg ${
                  page === p
                    ? 'bg-primary-600 text-white'
                    : 'text-secondary-600 dark:text-secondary-300 hover:bg-white dark:hover:bg-secondary-700'
                }`}
              >
                {p}
              </button>
            )
          )}
          <button
            type="button"
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-white dark:hover:bg-secondary-700 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  };

  // Pagination functions
  const getPaginatedData = (data, page, pageSize) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (data, pageSize) => {
    return Math.ceil(data.length / pageSize);
  };

  // Smart pagination function to show limited page numbers
  const getPaginationPages = (currentPage, totalPages) => {
    const maxVisiblePages = 7; // Show max 7 page numbers
    const pages = [];
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination with ellipsis
      if (currentPage <= 4) {
        // Show first 5 pages + ellipsis + last page
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        if (totalPages > 6) {
          pages.push('...');
        }
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show first page + ellipsis + last 5 pages
        pages.push(1);
        if (totalPages > 6) {
          pages.push('...');
        }
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page + ellipsis + current-1, current, current+1 + ellipsis + last page
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Reset pagination when filters change
  useEffect(() => {
    setProjectsPage(1);
  }, [projectsSearch]);

  useEffect(() => {
    setTasksPage(1);
  }, [tasksSearch, tasksStatusFilter]);

  useEffect(() => {
    setTimeEntriesPage(1);
  }, [timeEntriesSearch, timeEntriesDateFilter]);

  // Fetch team leads for the edit modal
  const fetchTeamLeads = async () => {
    try {
      const response = await axios.get('/api/users/all?include_admins=true');
      const teamLeads = response.data.filter(u => u.role === 'admin');
      setAvailableTeamLeads(teamLeads);
    } catch (error) {
      console.error('Failed to fetch team leads:', error);
    }
  };

  // Handle edit user modal
  const handleEditUser = () => {
    setEditFormData({
      department: user.department || '',
      team_lead_id: user.team_lead_id || '',
      is_disabled: user.is_disabled || false
    });
    fetchTeamLeads();
    setShowEditModal(true);
  };

  // Save user changes
  const handleSaveUser = async () => {
    try {
      setSaving(true);
      await axios.patch(`/api/users/${userId}`, editFormData);
      await fetchUser();
      setShowEditModal(false);
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user?.username) {
      toast.error('Username is missing for this user');
      return;
    }
    try {
      setResettingPassword(true);
      const { data } = await axios.post('/api/users/admin/generate-password', {
        username: user.username,
      });
      setShowResetPasswordConfirm(false);
      setGeneratedPassword(data.generated_password);
      setPasswordCopied(false);
      toast.success('Password reset successfully');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCopyGeneratedPassword = async () => {
    if (!generatedPassword) return;
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setPasswordCopied(true);
      toast.success('Password copied to clipboard');
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (error) {
      toast.error('Could not copy password');
    }
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3" />
          <p className="text-sm text-secondary-600 dark:text-secondary-400">Loading user details…</p>
        </div>
      </div>
    );
  }

  if (userError || !user) {
    return (
      <EmptyState
        icon={User}
        title="User not found"
        description="The user you're looking for doesn't exist or you don't have access."
        actionLabel="Back to Users"
        onAction={() => navigate('/users')}
      />
    );
  }

  const filteredProjects = getFilteredProjects();
  const filteredTasks = getFilteredTasks();

  return (
    <>
      <div className="space-y-5">
        <Button
          variant="ghost"
          onClick={() => navigate('/users')}
          className="!px-0 !py-0 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Button>

        <div className="page-header !mb-0">
          <div className="flex items-center gap-4 min-w-0">
            <Avatar
              blobName={user.avatar}
              alt={user.full_name || user.username || 'User'}
              size="xl"
              className="w-14 h-14 flex-shrink-0"
              previewable
              fallbackText={user.full_name?.charAt(0) || user.username?.charAt(0) || 'U'}
            />
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-secondary-900 dark:text-white truncate">
                {user.full_name || user.username}
              </h1>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 truncate">{user.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <StatusPill
                  status={roleLabel(user.role)}
                  colorClass={ROLE_PILL_CLASS[user.role] || ROLE_PILL_CLASS.member}
                  showDot={false}
                />
                <StatusPill
                  status={user.is_disabled ? 'At Risk' : 'Completed'}
                  label={user.is_disabled ? 'Disabled' : 'Active'}
                  showDot
                />
                {user.department && (
                  <span className="text-xs text-secondary-600 dark:text-secondary-300 bg-secondary-100 dark:bg-secondary-700 px-2 py-1 rounded-md">
                    {user.department}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => setShowSendNotification(true)}>
              <Bell className="h-4 w-4" />
              Send Notification
            </Button>
            <Button variant="secondary" onClick={() => setShowResetPasswordConfirm(true)}>
              <KeyRound className="h-4 w-4" />
              Reset Password
            </Button>
            <Button variant="primary" onClick={handleEditUser}>
              <Edit className="h-4 w-4" />
              Edit User
            </Button>
          </div>
        </div>

        <ViewTabs tabs={USER_TABS} activeId={activeTab} onChange={setActiveTab} />

        {activeTab === 'overview' && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={FolderOpen} tone="primary" label="Projects" value={projects.length} />
              <StatCard icon={CheckSquare} tone="green" label="Tasks" value={tasks.length} />
              <StatCard icon={Clock} tone="purple" label="Time entries" value={timeEntriesTotal} />
              <StatCard
                icon={Calendar}
                tone="amber"
                label="Last login"
                value={formatLastLogin(user.last_login)}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel>
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">
                  User information
                </h3>
                <dl className="space-y-3 text-sm">
                  {[
                    { icon: Mail, label: 'Email', value: user.email },
                    { icon: User, label: 'Username', value: user.username },
                    {
                      icon: Shield,
                      label: '2FA',
                      value: (
                        <StatusPill
                          status={user.two_factor_enabled ? 'Completed' : 'At Risk'}
                          label={user.two_factor_enabled ? 'Enabled' : 'Disabled'}
                          showDot
                        />
                      ),
                    },
                    {
                      icon: Github,
                      label: 'GitHub',
                      value: (
                        <StatusPill
                          status={user.github_connected ? 'Completed' : 'Not Started'}
                          label={user.github_connected ? 'Connected' : 'Not connected'}
                          showDot
                        />
                      ),
                    },
                    {
                      icon: Calendar,
                      label: 'Outlook',
                      value: (
                        <StatusPill
                          status={user.microsoft_calendar_connected ? 'Completed' : 'Not Started'}
                          label={user.microsoft_calendar_connected ? 'Connected' : 'Not connected'}
                          showDot
                        />
                      ),
                    },
                    {
                      icon: Users,
                      label: 'Team lead',
                      value: user.team_lead_name || 'Not assigned',
                    },
                    {
                      icon: AlertTriangle,
                      label: 'Status',
                      value: (
                        <StatusPill
                          status={user.is_disabled ? 'At Risk' : 'Completed'}
                          label={user.is_disabled ? 'Disabled' : 'Active'}
                          showDot
                        />
                      ),
                    },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-center justify-between gap-3">
                      <dt className="flex items-center gap-2 text-secondary-500 dark:text-secondary-400 min-w-0">
                        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                        {label}
                      </dt>
                      <dd className="font-medium text-secondary-900 dark:text-white text-right truncate max-w-[60%]">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </Panel>

              <Panel>
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">
                  Activity summary
                </h3>
                <dl className="space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-secondary-500 dark:text-secondary-400">Account created</dt>
                    <dd className="font-medium text-secondary-900 dark:text-white">
                      {user.created_at ? format(new Date(user.created_at), 'MMM dd, yyyy') : 'N/A'}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-secondary-500 dark:text-secondary-400">Last login</dt>
                    <dd className="font-medium text-secondary-900 dark:text-white">
                      {formatLastLogin(user.last_login)}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-secondary-500 dark:text-secondary-400">Role</dt>
                    <dd>
                      <StatusPill
                        status={roleLabel(user.role)}
                        colorClass={ROLE_PILL_CLASS[user.role] || ROLE_PILL_CLASS.member}
                        showDot={false}
                      />
                    </dd>
                  </div>
                </dl>
              </Panel>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-4">
            <Panel className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Analytics</h3>
                <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                  Productivity and leave signals for this user
                </p>
              </div>
              <select
                value={analyticsTimePeriod}
                onChange={(e) => setAnalyticsTimePeriod(e.target.value)}
                className="px-3 py-1.5 text-sm border border-secondary-200 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-800 dark:text-secondary-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                aria-label="Analytics time period"
              >
                <option value="7_days">Last 7 days</option>
                <option value="30_days">Last 30 days</option>
                <option value="90_days">Last 90 days</option>
                <option value="1_year">Last year</option>
              </select>
            </Panel>

            <Panel className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      (complianceSummary?.consecutive_under || 0) >= 2
                        ? 'bg-amber-100 dark:bg-amber-900/30'
                        : 'bg-primary-50 dark:bg-primary-900/30'
                    }`}
                  >
                    {(complianceSummary?.weeks_under || 0) > 0 ? (
                      <AlertTriangle
                        className={`h-4 w-4 ${
                          (complianceSummary?.consecutive_under || 0) >= 2
                            ? 'text-amber-600'
                            : 'text-primary-600'
                        }`}
                      />
                    ) : (
                      <Target className="h-4 w-4 text-primary-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
                      Weekly hours history
                    </h3>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                      {weeklyComplianceLoading
                        ? `Loading ${weeklyTarget}h compliance…`
                        : complianceSummary?.recommendation ||
                          `Last 4 weeks vs ${weeklyTarget}h weekly target`}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="text-xs py-1.5 px-3"
                  onClick={() => setActiveTab('timesheet')}
                >
                  View Timesheet
                </Button>
              </div>

              {weeklyComplianceLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-secondary-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                  Loading weekly compliance…
                </div>
              ) : complianceWeeks.length === 0 ? (
                <p className="text-sm text-secondary-500 dark:text-secondary-400 py-2">
                  No weekly hours data available for this user.
                </p>
              ) : (
                <div className="space-y-2.5">
                  {complianceWeeks.map((week) => {
                    const progress = Math.min(
                      ((week.total_hours || 0) / (week.target || weeklyTarget)) * 100,
                      100
                    );
                    const pill = compliancePill(week.status);
                    return (
                      <div
                        key={week.week_start}
                        className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-lg border border-secondary-100 dark:border-secondary-700 px-3 py-2.5"
                      >
                        <div className="w-full sm:w-36 shrink-0 text-xs font-medium text-secondary-700 dark:text-secondary-300">
                          {formatWeekRange(week.week_start, week.week_end)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="h-2 bg-secondary-100 dark:bg-secondary-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${complianceBarClass(week.status)}`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                          <span className="text-xs font-medium text-secondary-800 dark:text-secondary-200 tabular-nums">
                            {(week.total_hours || 0).toFixed(1)}h / {week.target || weeklyTarget}h
                          </span>
                          <StatusPill status={pill.status} label={pill.label} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>

            {!analyticsLoading && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard
                  icon={Clock}
                  tone="primary"
                  label="Total hours"
                  value={(analyticsData.efficiencyMetrics.totalHours || 0).toFixed(1)}
                />
                <StatCard
                  icon={CheckSquare}
                  tone="green"
                  label="Completed tasks"
                  value={analyticsData.efficiencyMetrics.completedTasks || 0}
                />
                <StatCard
                  icon={Zap}
                  tone="purple"
                  label="Tasks / hour"
                  value={analyticsData.efficiencyMetrics.tasksPerHour || 0}
                />
                <StatCard
                  icon={Target}
                  tone="amber"
                  label="Productivity"
                  value={`${analyticsData.efficiencyMetrics.productivityScore || 0}%`}
                />
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel>
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3 flex items-center gap-2">
                  <PieChart className="h-4 w-4 text-primary-600" />
                  Task status
                </h3>
                {analyticsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                  </div>
                ) : Object.keys(analyticsData.taskStatusBreakdown || {}).length === 0 ? (
                  <EmptyState
                    icon={PieChart}
                    title="No task status data"
                    description="Task activity will appear here once work is logged."
                    className="!py-8"
                  />
                ) : (
                  <div className="space-y-2.5">
                    {Object.entries(analyticsData.taskStatusBreakdown).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between gap-3">
                        <StatusPill
                          status={taskStatusPill(status)}
                          label={status.replace(/_/g, ' ')}
                        />
                        <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              <Panel>
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-primary-600" />
                  Leave performance
                </h3>
                {analyticsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                  </div>
                ) : (
                  <dl className="space-y-2.5 text-sm">
                    {[
                      ['Sick leaves', analyticsData.leavePerformance.sickLeaves || 0],
                      ['Annual leaves', analyticsData.leavePerformance.annualLeaves || 0],
                      ['Utilization', `${analyticsData.leavePerformance.utilization || 0}%`],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="flex items-center justify-between rounded-lg bg-secondary-50 dark:bg-secondary-900/40 px-3 py-2"
                      >
                        <dt className="text-secondary-600 dark:text-secondary-400">{label}</dt>
                        <dd className="font-semibold text-secondary-900 dark:text-white">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </Panel>

              <Panel>
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3 flex items-center gap-2">
                  <Timer className="h-4 w-4 text-primary-600" />
                  Time tracking
                </h3>
                {analyticsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                  </div>
                ) : (
                  <dl className="space-y-2.5 text-sm">
                    {[
                      [
                        'Hours logged',
                        `${(analyticsData.efficiencyMetrics.totalHours || 0).toFixed(1)}h`,
                      ],
                      [
                        'Avg daily hours',
                        `${((analyticsData.efficiencyMetrics.totalHours || 0) / 30).toFixed(1)}h`,
                      ],
                      ['Time entries', analyticsData.timeEntries.length],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="flex items-center justify-between rounded-lg bg-secondary-50 dark:bg-secondary-900/40 px-3 py-2"
                      >
                        <dt className="text-secondary-600 dark:text-secondary-400">{label}</dt>
                        <dd className="font-semibold text-secondary-900 dark:text-white">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </Panel>

              <Panel>
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-primary-600" />
                  Productivity trends
                </h3>
                {analyticsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                  </div>
                ) : analyticsData.productivityTrends.length === 0 ? (
                  <EmptyState
                    icon={BarChart3}
                    title="No productivity data"
                    description={`Time entries in period: ${analyticsData.timeEntries.length}`}
                    className="!py-8"
                  />
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-end justify-between h-36 px-1 border-b border-secondary-200 dark:border-secondary-700">
                      {analyticsData.productivityTrends.map((trend, index) => {
                        const maxHours = Math.max(
                          ...analyticsData.productivityTrends.map((t) => t.hours)
                        );
                        const height = maxHours > 0 ? (trend.hours / maxHours) * 112 : 0;
                        const date = new Date(trend.date);
                        return (
                          <div key={index} className="flex flex-col items-center flex-1 min-w-0">
                            <div
                              className="bg-primary-500 rounded-t w-full max-w-[1.75rem] mb-1.5 hover:bg-primary-600 transition-colors"
                              style={{ height: `${height}px` }}
                              title={`${trend.date}: ${trend.hours.toFixed(1)}h`}
                            />
                            <span className="text-[10px] text-secondary-500 dark:text-secondary-400 truncate">
                              {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-center text-xs text-secondary-500 dark:text-secondary-400">
                      Daily hours ({analyticsData.productivityTrends.length} days)
                    </p>
                  </div>
                )}
              </Panel>
            </div>
          </div>
        )}

        {activeTab === 'timesheet' && (
          <Timesheet lockedUserId={userId} />
        )}

        {activeTab === 'projects' && (
          <Panel padding={false} className="overflow-hidden">
            <div className="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FolderOpen className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Projects</h3>
                <span className="text-xs text-secondary-500 dark:text-secondary-400 bg-secondary-100 dark:bg-secondary-700 px-2 py-0.5 rounded-full">
                  {filteredProjects.length}
                </span>
              </div>
              <div className="relative w-full sm:w-64">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search projects…"
                  value={projectsSearch}
                  onChange={(e) => {
                    setProjectsSearch(e.target.value);
                    setProjectsPage(1);
                  }}
                  className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {projectsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : filteredProjects.length > 0 ? (
              <>
                <div className="md:hidden divide-y divide-secondary-100 dark:divide-secondary-700">
                  {getPaginatedData(filteredProjects, projectsPage, pageSize).map((project) => (
                    <button
                      key={project.id}
                      type="button"
                      onClick={() => navigate(`/projects/${project.id}`)}
                      className="w-full text-left p-4 hover:bg-secondary-50 dark:hover:bg-secondary-700/30 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                          {project.name}
                        </p>
                        <ExternalLink className="h-4 w-4 text-secondary-400 shrink-0" />
                      </div>
                      {project.description && (
                        <p className="text-xs text-secondary-500 line-clamp-2">{project.description}</p>
                      )}
                      <StatusPill
                        status={projectStatusPill(project.status)}
                        label={(project.status || 'Active').replace(/_/g, ' ')}
                      />
                    </button>
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50/80 dark:bg-secondary-800/80">
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400">
                          Project
                        </th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400 w-36">
                          Status
                        </th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400 w-20">
                          Open
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700/60">
                      {getPaginatedData(filteredProjects, projectsPage, pageSize).map((project) => (
                        <tr
                          key={project.id}
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className="hover:bg-secondary-50/70 dark:hover:bg-secondary-700/30 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                                {project.name}
                              </p>
                              {project.description && (
                                <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate mt-0.5">
                                  {project.description}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill
                              status={projectStatusPill(project.status)}
                              label={(project.status || 'Active').replace(/_/g, ' ')}
                            />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <ExternalLink className="h-4 w-4 text-secondary-400 inline-block" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderListPagination({
                  page: projectsPage,
                  setPage: setProjectsPage,
                  total: filteredProjects.length,
                  label: 'projects',
                })}
              </>
            ) : (
              <EmptyState
                icon={FolderOpen}
                title={projectsSearch ? 'No matching projects' : 'No projects assigned'}
                description={
                  projectsSearch
                    ? 'Try a different search term.'
                    : 'This user is not on any projects yet.'
                }
              />
            )}
          </Panel>
        )}

        {activeTab === 'tasks' && (
          <Panel padding={false} className="overflow-hidden">
            <div className="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700 flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <CheckSquare className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Tasks</h3>
                <span className="text-xs text-secondary-500 dark:text-secondary-400 bg-secondary-100 dark:bg-secondary-700 px-2 py-0.5 rounded-full">
                  {filteredTasks.length}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                <div className="relative flex-1 sm:w-56">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-secondary-400" />
                  <input
                    type="text"
                    placeholder="Search tasks…"
                    value={tasksSearch}
                    onChange={(e) => {
                      setTasksSearch(e.target.value);
                      setTasksPage(1);
                    }}
                    className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="relative sm:w-40">
                  <Filter className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-secondary-400 pointer-events-none" />
                  <select
                    value={tasksStatusFilter}
                    onChange={(e) => {
                      setTasksStatusFilter(e.target.value);
                      setTasksPage(1);
                    }}
                    className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                  >
                    <option value="all">All status</option>
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="In QA">In QA</option>
                    <option value="Done">Done</option>
                    <option value="Blocked">Blocked</option>
                  </select>
                </div>
              </div>
            </div>

            {tasksLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : filteredTasks.length > 0 ? (
              <>
                <div className="md:hidden divide-y divide-secondary-100 dark:divide-secondary-700">
                  {getPaginatedData(filteredTasks, tasksPage, pageSize).map((task) => (
                    <button
                      key={task.id}
                      type="button"
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      className="w-full text-left p-4 hover:bg-secondary-50 dark:hover:bg-secondary-700/30 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                          {task.name}
                        </p>
                        <ExternalLink className="h-4 w-4 text-secondary-400 shrink-0" />
                      </div>
                      <p className="text-xs text-secondary-500 truncate">{task.project_name || '—'}</p>
                      <div className="flex flex-wrap gap-2">
                        <StatusPill status={task.status || 'To Do'} />
                        {(task.priority_level || task.priority) && (
                          <span className="text-xs text-secondary-500 capitalize">
                            {task.priority_level || task.priority}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50/80 dark:bg-secondary-800/80">
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400">
                          Task
                        </th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400 w-40">
                          Project
                        </th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400 w-36">
                          Status
                        </th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400 w-28">
                          Priority
                        </th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400 w-20">
                          Open
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700/60">
                      {getPaginatedData(filteredTasks, tasksPage, pageSize).map((task) => (
                        <tr
                          key={task.id}
                          onClick={() => navigate(`/tasks/${task.id}`)}
                          className="hover:bg-secondary-50/70 dark:hover:bg-secondary-700/30 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                                {task.name}
                              </p>
                              {task.description && (
                                <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate mt-0.5">
                                  {task.description}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-secondary-600 dark:text-secondary-300 truncate block max-w-[10rem]">
                              {task.project_name || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill
                              status={taskStatusPill(task.status)}
                              label={task.status || 'To Do'}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs capitalize text-secondary-600 dark:text-secondary-300">
                              {task.priority_level || task.priority || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <ExternalLink className="h-4 w-4 text-secondary-400 inline-block" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderListPagination({
                  page: tasksPage,
                  setPage: setTasksPage,
                  total: filteredTasks.length,
                  label: 'tasks',
                })}
              </>
            ) : (
              <EmptyState
                icon={CheckSquare}
                title={
                  tasksSearch || tasksStatusFilter !== 'all'
                    ? 'No matching tasks'
                    : 'No tasks assigned'
                }
                description={
                  tasksSearch || tasksStatusFilter !== 'all'
                    ? 'Clear filters to see more.'
                    : 'This user has no assigned tasks yet.'
                }
              />
            )}
          </Panel>
        )}

        {activeTab === 'time-entries' && (
          <Panel padding={false} className="overflow-hidden">
            <div className="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700 flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Clock className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Time entries</h3>
                <span className="text-xs text-secondary-500 dark:text-secondary-400 bg-secondary-100 dark:bg-secondary-700 px-2 py-0.5 rounded-full">
                  {timeEntriesListTotal}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                <div className="relative flex-1 sm:w-56">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-secondary-400" />
                  <input
                    type="text"
                    placeholder="Search entries…"
                    value={timeEntriesSearch}
                    onChange={(e) => {
                      setTimeEntriesSearch(e.target.value);
                      setTimeEntriesPage(1);
                    }}
                    className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white placeholder:text-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="relative sm:w-40">
                  <Filter className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-secondary-400 pointer-events-none" />
                  <select
                    value={timeEntriesDateFilter}
                    onChange={(e) => {
                      setTimeEntriesDateFilter(e.target.value);
                      setTimeEntriesPage(1);
                    }}
                    className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                  >
                    <option value="all">All time</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="this_week">This week</option>
                  </select>
                </div>
              </div>
            </div>

            {timeEntriesLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : timeEntries.length > 0 ? (
              <>
                <div className="md:hidden divide-y divide-secondary-100 dark:divide-secondary-700">
                  {timeEntries.map((entry) => (
                    <div key={entry.id} className="p-4 space-y-1">
                      <div className="flex justify-between gap-2">
                        <span className="text-sm font-medium text-secondary-900 dark:text-white">
                          {toUTCPlus5(entry.start_time)?.split(',').slice(0, 2).join(',') || '—'}
                        </span>
                        <span className="text-sm font-semibold text-primary-600 tabular-nums">
                          {formatEntryDuration(entry.duration)}
                        </span>
                      </div>
                      <p className="text-xs text-secondary-500 truncate">
                        {entry.task_name || '—'} · {entry.project_name || '—'}
                      </p>
                      {entry.description && (
                        <p className="text-xs text-secondary-400 line-clamp-2">{entry.description}</p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50/80 dark:bg-secondary-800/80">
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400">
                          Date / time
                        </th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400">
                          Task / project
                        </th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400">
                          Description
                        </th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400 w-28">
                          Duration
                        </th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-secondary-500 dark:text-secondary-400 w-28">
                          Type
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700/60">
                      {timeEntries.map((entry) => (
                        <tr
                          key={entry.id}
                          className="hover:bg-secondary-50/70 dark:hover:bg-secondary-700/30 transition-colors"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <p className="text-sm font-medium text-secondary-900 dark:text-white">
                              {entry.start_time
                                ? toUTCPlus5(entry.start_time).split(',').slice(0, 2).join(',')
                                : '—'}
                            </p>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                              {entry.start_time ? toUTCPlus5Time(entry.start_time) : '—'}
                              {entry.end_time ? ` – ${toUTCPlus5Time(entry.end_time)}` : ' – Running'}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-secondary-900 dark:text-white truncate max-w-[14rem]">
                              {entry.task_name || 'No task'}
                            </p>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate max-w-[14rem] mt-0.5">
                              {entry.project_name || 'No project'}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-xs text-secondary-600 dark:text-secondary-300 line-clamp-2 max-w-xs">
                              {entry.description || '—'}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-semibold text-primary-600 dark:text-primary-400 tabular-nums">
                              {formatEntryDuration(entry.duration)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <StatusPill status={entry.is_manual ? 'Manual' : 'Tracked'} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderListPagination({
                  page: timeEntriesPage,
                  setPage: setTimeEntriesPage,
                  total: timeEntriesListTotal,
                  label: 'entries',
                })}
              </>
            ) : (
              <EmptyState
                icon={Clock}
                title={
                  timeEntriesSearch || timeEntriesDateFilter !== 'all'
                    ? 'No matching entries'
                    : 'No time entries'
                }
                description={
                  timeEntriesSearch || timeEntriesDateFilter !== 'all'
                    ? 'Try adjusting the date or search.'
                    : 'This user has not logged time yet.'
                }
              />
            )}
          </Panel>
        )}
      </div>

      <ConfirmModal
        open={showResetPasswordConfirm}
        title="Reset Password"
        message={`Generate a new password for ${user.full_name || user.username}? Their current password will stop working immediately.`}
        confirmText="Reset Password"
        cancelText="Cancel"
        confirmButtonStyle="danger"
        isLoading={resettingPassword}
        onConfirm={handleResetPassword}
        onCancel={() => {
          if (!resettingPassword) setShowResetPasswordConfirm(false);
        }}
      />

      <FormDrawer
        open={Boolean(generatedPassword)}
        onClose={() => {
          setGeneratedPassword(null);
          setPasswordCopied(false);
        }}
        title="New Password Generated"
        subtitle={`Share this password securely with ${user.full_name || user.username}. It will not be shown again.`}
        icon={<KeyRound className="h-5 w-5" />}
        iconColor="primary"
        widthClass="sm:max-w-md"
        footer={(
          <button
            type="button"
            onClick={() => {
              setGeneratedPassword(null);
              setPasswordCopied(false);
            }}
            className="btn-primary"
          >
            Done
          </button>
        )}
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
            Copy and share this password now. For security, it is only shown once.
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Temporary password
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={generatedPassword || ''}
                className="input-field font-mono text-sm"
              />
              <button
                type="button"
                onClick={handleCopyGeneratedPassword}
                className="btn-secondary flex items-center gap-1.5 flex-shrink-0"
                aria-label="Copy password"
              >
                {passwordCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {passwordCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      </FormDrawer>

      {/* Edit User Drawer */}
      <FormDrawer
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit User"
        subtitle="Update user settings and assignments."
        icon={<Edit className="h-5 w-5" />}
        iconColor="primary"
        widthClass="sm:max-w-md lg:max-w-lg"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setShowEditModal(false)}
              className="btn-secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveUser}
              disabled={saving}
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </>
        )}
      >
            <div className="space-y-4">
              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Department
                </label>
                <input
                  type="text"
                  value={editFormData.department}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, department: e.target.value }))}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter department"
                />
              </div>

              {/* Team Lead */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Team Lead
                </label>
                <select
                  value={editFormData.team_lead_id}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, team_lead_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Team Lead</option>
                  {availableTeamLeads.map(lead => (
                    <option key={lead.id} value={lead.id}>
                      {lead.full_name || lead.username}
                    </option>
                  ))}
                </select>
              </div>

              {/* Disable User */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="disable-user"
                  checked={editFormData.is_disabled}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, is_disabled: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                />
                <label htmlFor="disable-user" className="text-sm font-medium text-secondary-700">
                  Disable User Account
                </label>
              </div>
            </div>
      </FormDrawer>

      <SendNotificationModal
        open={showSendNotification}
        onClose={() => setShowSendNotification(false)}
        targetUserId={user?.id || userId}
        targetUserName={user?.full_name || user?.username}
      />
    </>
  );
};

export default UserDetail;
